import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import DashboardLayout from './components/Layout/DashboardLayout';
import LoginScreen from './components/Auth/LoginScreen';
import SignupPage from './components/Auth/SignupPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Spinner from './components/UI/Spinner';

// Lazy load components for code splitting
const WhatsAppDashboard = lazy(() => import('./components/WhatsApp/WhatsAppDashboard'));
const MessageTemplatesPage = lazy(() => import('./components/MessageTemplates/MessageTemplatesPage'));
const TemplateSendingView = lazy(() => import('./components/Templates/TemplateSendingView'));
const BulkCampaignHistory = lazy(() => import('./components/Templates/BulkCampaignHistory'));
const AIPersonaPage = lazy(() => import('./components/AI/AIPersonaPage'));
const AgentReportsPage = lazy(() => import('./components/Analytics/AgentReportsPage'));
const PhonebookPage = lazy(() => import('./components/Phonebook/PhonebookPage'));
const AnalyticsPage = lazy(() => import('./components/Analytics/AnalyticsPage'));
const AgentTriggersPage = lazy(() => import('./components/Analytics/AgentTriggersPage'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const TestChatPage = lazy(() => import('./components/TestChat/TestChatPage'));
const MediaPage = lazy(() => import('./components/Media/MediaPage'));
const TeamManagementPage = lazy(() => import('./components/Team/TeamManagementPage'));
const TeamAccessPage = lazy(() => import('./components/Team/TeamAccessPage'));
const AddonsPage = lazy(() => import('./components/Addons/AddonsPage'));
const ZohoCRMDashboard = lazy(() => import('./components/Addons/ZohoCRM/ZohoCRMDashboard'));
const ZohoOAuthCallback = lazy(() => import('./components/Addons/ZohoCRM/ZohoOAuthCallback'));
const GoogleCalendarDashboard = lazy(() => import('./components/Addons/GoogleCalendar/GoogleCalendarDashboard'));
const GoogleCalendarSetup = lazy(() => import('./components/Addons/GoogleCalendar/GoogleCalendarSetup'));
const GoogleCalendarSettings = lazy(() => import('./components/Addons/GoogleCalendar/GoogleCalendarSettings'));
const GoogleCalendarOAuthCallback = lazy(() => import('./components/Addons/GoogleCalendar/GoogleCalendarOAuthCallback'));
const XeroDashboard = lazy(() => import('./components/Addons/Xero/XeroDashboard'));
const XeroOAuthCallback = lazy(() => import('./components/Addons/Xero/XeroOAuthCallback'));
const WhatsAppAddonDashboard = lazy(() => import('./components/Addons/WhatsApp/WhatsAppAddonDashboard'));
const LeadsDashboard = lazy(() => import('./components/Addons/Leads/LeadsDashboard'));
const ClientsDashboard = lazy(() => import('./components/Addons/Leads/ClientsDashboard'));
const DataPage = lazy(() => import('./components/Data/DataPage'));
const IntelligencePage = lazy(() => import('./components/Intelligence/IntelligencePage'));

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <Spinner />
  </div>
);

// Wrapper component for lazy loaded routes with error boundary
const LazyRouteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginScreen />,
    errorElement: <Navigate to="/login" replace />
  },
  {
    path: '/signup',
    element: <SignupPage />,
    errorElement: <Navigate to="/login" replace />
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <Navigate to="/login" replace />,
    children: [
      {
        index: true,
        element: <Navigate to="/addons" replace />
      },
      {
        path: 'whatsapp',
        element: (
          <LazyRouteWrapper>
            <WhatsAppDashboard />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'ai-persona',
        element: (
          <LazyRouteWrapper>
            <AIPersonaPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'agent-reports',
        element: (
          <LazyRouteWrapper>
            <AgentReportsPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'templates',
        element: (
          <LazyRouteWrapper>
            <MessageTemplatesPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'send-templates',
        element: (
          <LazyRouteWrapper>
            <TemplateSendingView />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'campaign-history',
        element: (
          <LazyRouteWrapper>
            <BulkCampaignHistory />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'phonebook',
        element: (
          <LazyRouteWrapper>
            <PhonebookPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'analytics',
        element: (
          <LazyRouteWrapper>
            <AnalyticsPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'agent-triggers',
        element: (
          <LazyRouteWrapper>
            <AgentTriggersPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'help',
        element: (
          <LazyRouteWrapper>
            <HelpPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'media',
        element: (
          <LazyRouteWrapper>
            <MediaPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'test-chat',
        element: (
          <LazyRouteWrapper>
            <TestChatPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'team',
        element: (
          <LazyRouteWrapper>
            <TeamManagementPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'team-access',
        element: (
          <LazyRouteWrapper>
            <TeamAccessPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons',
        element: (
          <LazyRouteWrapper>
            <AddonsPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/zoho-crm',
        element: (
          <LazyRouteWrapper>
            <ZohoCRMDashboard />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/google-calendar',
        element: (
          <LazyRouteWrapper>
            <GoogleCalendarDashboard />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/google-calendar/setup',
        element: (
          <LazyRouteWrapper>
            <GoogleCalendarSetup />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/google-calendar/settings',
        element: (
          <LazyRouteWrapper>
            <GoogleCalendarSettings />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/xero',
        element: (
          <LazyRouteWrapper>
            <XeroDashboard />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/whatsapp',
        element: (
          <LazyRouteWrapper>
            <WhatsAppAddonDashboard />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/leads',
        element: (
          <LazyRouteWrapper>
            <LeadsDashboard />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'addons/crm-clients',
        element: (
          <LazyRouteWrapper>
            <ClientsDashboard />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'data',
        element: (
          <LazyRouteWrapper>
            <DataPage />
          </LazyRouteWrapper>
        )
      },
      {
        path: 'intelligence',
        element: (
          <LazyRouteWrapper>
            <IntelligencePage />
          </LazyRouteWrapper>
        )
      }
    ]
  },
  {
    path: '/addons/zoho/callback',
    element: (
      <LazyRouteWrapper>
        <ZohoOAuthCallback />
      </LazyRouteWrapper>
    )
  },
  {
    path: '/addons/google-calendar/callback',
    element: (
      <LazyRouteWrapper>
        <GoogleCalendarOAuthCallback />
      </LazyRouteWrapper>
    )
  },
  {
    path: '/addons/xero/callback',
    element: (
      <LazyRouteWrapper>
        <XeroOAuthCallback />
      </LazyRouteWrapper>
    )
  },
  {
    path: '*',
    element: <Navigate to="/" replace />
  }
]);

export default router;